<template>
    <div class="site-width uk-animation-fade">
        <Seo
            :canonical="'/family-registration/' + slug"
            :title="offer ? offer.name : 'Anmeldung'" />

        <div class="menu-headline-distance" />

        <div v-if="loading" class="uk-text-center">
            <div uk-spinner />
        </div>

        <template v-else-if="tokenInvalid">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Ungültiger Zugangscode."
                    message="Bitte verwende den Link aus deiner Einladung." />
            </div>
        </template>

        <template v-else-if="error || !offer">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Angebot nicht gefunden."
                    message="Bitte versuche es später erneut." />
            </div>
        </template>

        <template v-else-if="!offer.registration_open">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    :headline="offer.name"
                    message="Die Anmeldung ist leider geschlossen." />
            </div>
        </template>

        <template v-else-if="success">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    is-main
                    switch-headline
                    :message="'Danke, ' + form.forename + '!'"
                    :headline="
                        offer.is_waitlist
                            ? 'Du stehst auf der Warteliste. Wir melden uns, sobald ein Platz frei wird.'
                            : 'Deine Anmeldung wurde erfolgreich übermittelt. Wir melden uns bei dir mit weiteren Informationen.'
                    " />
            </div>
        </template>

        <template v-else>
            <div class="uk-grid uk-grid-large" uk-grid>
                <!-- Left: Info -->
                <div class="uk-width-1-2@m">
                    <div class="eyebrow">Für eine Kindheit ohne Krebs</div>
                    <h1 class="hero-headline">
                        Die Auszeit, die ihr euch verdient habt.
                    </h1>
                    <p class="hero-text">
                        Direkt auf dem Deich an der Wurster Nordseeküste, mit
                        Blick aufs Meer – Cancel Cancer schenkt Familien
                        krebskranker Kinder eine echte Auszeit im
                        <strong>Upleven in Wremen</strong> – dem einzigen Hotel
                        Deutschlands, das direkt auf dem Deich steht.
                    </p>
                    <div class="info-items">
                        <div class="info-item">
                            <div class="info-icon">📅</div>
                            <div>
                                <div class="info-label">Zeitraum</div>
                                <div class="info-value">
                                    6. bis 9. August · 3 Nächte
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🍽️</div>
                            <div>
                                <div class="info-label">Verpflegung</div>
                                <div class="info-value">
                                    Vollpension inklusive
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🌊</div>
                            <div>
                                <div class="info-label">Lage</div>
                                <div class="info-value">
                                    Direkte Deich- und Strandlage, Wattenmeer
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🎯</div>
                            <div>
                                <div class="info-label">Programm</div>
                                <div class="info-value">
                                    Kinderyoga, Wattwanderung, Lagerfeuer & mehr
                                </div>
                            </div>
                        </div>
                    </div>
                    <a
                        href="/assets/upleven.pdf"
                        target="_blank"
                        rel="noopener"
                        class="btn-info">
                        Alle Infos als PDF →
                    </a>
                </div>

                <!-- Right: Form -->
                <div class="uk-width-1-2@m">
                    <div class="uk-card uk-card-alternative uk-padding">
                        <h2 class="form-title uk-margin-bottom">
                            Jetzt anmelden
                        </h2>
                        <p class="form-sub">
                            Die Plätze sind begrenzt. Wir melden uns zur
                            Bestätigung.
                        </p>

                        <!-- WAITLIST BANNER -->
                        <div v-if="offer.is_waitlist" class="waitlist-banner">
                            ⚠️ Alle Plätze sind aktuell vergeben – du meldest
                            dich auf die <strong>Warteliste</strong>. Falls ein
                            Platz frei wird, kontaktieren wir dich.
                        </div>

                        <span id="error-box" />
                        <InfoBox
                            mode="danger"
                            slim
                            medium
                            v-if="formError"
                            class="uk-margin-medium-bottom uk-animation-fade">
                            {{ formError }}
                        </InfoBox>

                        <div class="uk-grid uk-grid-small" uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label">Vorname</label>
                                <input
                                    v-model="form.forename"
                                    class="uk-input field-input"
                                    type="text" />
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Nachname</label>
                                <input
                                    v-model="form.surname"
                                    class="uk-input field-input"
                                    type="text" />
                            </div>
                        </div>

                        <div
                            class="uk-grid uk-grid-small uk-margin-top"
                            uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label"
                                    >E-Mail Adresse</label
                                >
                                <input
                                    v-model="form.email"
                                    class="uk-input field-input"
                                    :class="{'input-error': emailInvalid}"
                                    type="email"
                                    @blur="validateEmail" />
                                <div v-if="emailInvalid" class="field-error">
                                    Bitte eine gültige E-Mail-Adresse eingeben.
                                </div>
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Telefon</label>
                                <input
                                    v-model="form.phone"
                                    class="uk-input field-input"
                                    type="tel" />
                            </div>
                        </div>

                        <div class="uk-margin-medium-top">
                            <div class="guests-header">
                                <label
                                    class="field-label"
                                    style="margin-bottom: 0"
                                    >Teilnehmende Personen</label
                                >
                                <div class="guests-actions">
                                    <span
                                        @click="addGuest('adult')"
                                        class="guest-add-btn"
                                        >+ Erwachsener</span
                                    >
                                    <span
                                        @click="addGuest('child')"
                                        class="guest-add-btn"
                                        >+ Kind</span
                                    >
                                </div>
                            </div>

                            <div
                                v-if="form.guests.length === 0"
                                class="guests-empty">
                                Bitte füge alle Teilnehmer hinzu.
                            </div>

                            <div
                                v-for="(guest, i) in form.guests"
                                :key="i"
                                class="guest-card">
                                <div class="guest-card-header">
                                    <span
                                        class="guest-type-badge"
                                        :class="guest.type">
                                        {{
                                            guest.type === 'adult'
                                                ? 'Erwachsener'
                                                : 'Kind'
                                        }}
                                    </span>
                                    <span
                                        @click="removeGuest(i)"
                                        class="guest-remove"
                                        >✕</span
                                    >
                                </div>

                                <div
                                    class="uk-grid uk-grid-small uk-margin-small-top"
                                    uk-grid>
                                    <div class="uk-width-1-2">
                                        <label class="field-label"
                                            >Vorname & Nachname</label
                                        >
                                        <input
                                            v-model="guest.name"
                                            class="uk-input field-input"
                                            type="text" />
                                    </div>
                                    <div class="uk-width-1-2">
                                        <label class="field-label"
                                            >Geburtsdatum</label
                                        >
                                        <input
                                            :value="guest.birthdate"
                                            class="uk-input field-input"
                                            type="text"
                                            readonly
                                            placeholder="Datum wählen"
                                            @click="togglePicker(i)" />
                                        <div
                                            v-if="activePicker === i"
                                            class="uk-margin-small-top">
                                            <v-date-picker
                                                v-model="guest.birthdateRaw"
                                                is-dark
                                                @input="
                                                    onPickerInput(i, $event)
                                                " />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="guest.type === 'child'"
                                    class="uk-margin-small-top">
                                    <label class="field-label"
                                        >Schlafplatz</label
                                    >
                                    <div class="sleep-options">
                                        <div
                                            v-for="opt in sleepOptions"
                                            :key="opt.value"
                                            class="sleep-option"
                                            :class="{
                                                active:
                                                    guest.sleep_arrangement ===
                                                    opt.value,
                                            }"
                                            @click="
                                                guest.sleep_arrangement =
                                                    opt.value
                                            ">
                                            {{ opt.label }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Notes -->
                        <div class="uk-margin-top">
                            <label class="field-label"
                                >Anmerkungen (optional)</label
                            >
                            <textarea
                                v-model="form.notes"
                                class="uk-textarea field-input"
                                rows="3"
                                style="height: auto" />
                        </div>

                        <!-- Submit -->
                        <div class="uk-margin-medium-top uk-text-center">
                            <Button
                                is-large
                                has-action
                                :action="submit"
                                :disabled="submitting"
                                mode="success"
                                class="uk-width-1-1">
                                {{
                                    submitting
                                        ? 'Wird gesendet...'
                                        : offer.is_waitlist
                                        ? 'Auf Warteliste eintragen →'
                                        : 'Jetzt Anmelden →'
                                }}
                            </Button>
                        </div>

                        <div class="uk-margin-top uk-text-center consent-text">
                            Mit der Anmeldung akzeptierst du unsere
                            Teilnahmebedingungen und die Datenschutzerklärung.
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="uk-margin-large-top" />
    </div>
</template>

<script>
import Seo from '../../components/Seo.vue';
import SiteHeadline from '@/components/SiteHeadline.vue';
import Button from '../../components/Button.vue';
import InfoBox from '@/components/InfoBox.vue';
import axios from 'axios';
import UIkit from 'uikit';

export default {
    name: 'FamilyRegistration',
    components: {Seo, SiteHeadline, Button, InfoBox},
    data() {
        return {
            loading: true,
            error: null,
            tokenInvalid: false,
            offer: null,
            success: false,
            submitting: false,
            formError: null,
            emailInvalid: false,
            activePicker: null,
            form: {
                forename: '',
                surname: '',
                email: '',
                phone: '',
                guests: [],
                notes: '',
            },
            sleepOptions: [
                {value: 'eigenes_bett', label: 'Eigenes Bett'},
                {value: 'elternbett', label: 'Elternbett'},
                {value: 'babybett', label: 'Babybett'},
                {value: 'beistellbett', label: 'Beistellbett'},
            ],
        };
    },
    computed: {
        slug() {
            return this.$route.params.slug;
        },
        token() {
            return this.$route.query.token;
        },
        adultsCount() {
            return this.form.guests.filter((g) => g.type === 'adult').length;
        },
        childrenCount() {
            return this.form.guests.filter((g) => g.type === 'child').length;
        },
    },
    created() {
        this.loadOffer();
    },
    methods: {
        loadOffer() {
            this.loading = true;
            axios
                .get(
                    this.getBaseUrl + '/api/family-registration/' + this.slug,
                    {params: {token: this.token}}
                )
                .then((res) => {
                    this.offer = res.data;
                    this.loading = false;
                })
                .catch((e) => {
                    if (e.response && e.response.status === 403)
                        this.tokenInvalid = true;
                    else this.error = e;
                    this.loading = false;
                });
        },
        addGuest(type) {
            this.form.guests.push({
                name: '',
                birthdate: '',
                birthdateRaw: null,
                type,
                sleep_arrangement: null,
            });
        },
        removeGuest(i) {
            this.form.guests.splice(i, 1);
        },
        togglePicker(i) {
            this.activePicker = this.activePicker === i ? null : i;
        },
        onPickerInput(i, val) {
            this.form.guests[i].birthdateRaw = val;
            this.form.guests[i].birthdate = new Date(val).toLocaleDateString(
                'de-DE',
                {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }
            );
            this.activePicker = null;
        },
        validateEmail() {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.emailInvalid =
                this.form.email.length > 0 && !re.test(this.form.email);
        },
        submit() {
            this.formError = null;

            if (
                !this.form.forename ||
                !this.form.surname ||
                !this.form.email ||
                !this.form.phone
            ) {
                this.formError = 'Bitte fülle alle Kontaktfelder aus.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(this.form.email)) {
                this.emailInvalid = true;
                this.formError = 'Bitte eine gültige E-Mail-Adresse eingeben.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            if (this.form.guests.length === 0) {
                this.formError = 'Bitte füge mindestens eine Person hinzu.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            const missingGuest = this.form.guests.find(
                (g) => !g.name || !g.birthdate
            );
            if (missingGuest) {
                this.formError =
                    'Bitte fülle Name und Geburtsdatum für alle Personen aus.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            this.submitting = true;

            // Strip birthdateRaw before sending, convert birthdate to ISO
            const guests = this.form.guests.map((g) => ({
                name: g.name,
                birthdate: new Date(g.birthdateRaw).toISOString().split('T')[0],
                type: g.type,
                sleep_arrangement: g.sleep_arrangement,
            }));

            axios
                .post(
                    this.getBaseUrl +
                        '/api/family-registration/' +
                        this.slug +
                        '/register',
                    {
                        forename: this.form.forename,
                        surname: this.form.surname,
                        email: this.form.email,
                        phone: this.form.phone,
                        adults: this.adultsCount,
                        children: this.childrenCount,
                        guests,
                        notes: this.form.notes || undefined,
                    },
                    {params: {token: this.token}}
                )
                .then(() => {
                    this.success = true;
                    window.scrollTo({top: 0, behavior: 'smooth'});
                })
                .catch((e) => {
                    this.formError =
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
                    console.error(e);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #2a9d6e;
    margin-bottom: 12px;
}

.hero-headline {
    color: #fff;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.15;
    margin: 0 0 20px;
}

.accent {
    color: #2a9d6e;
    font-style: italic;
}

.hero-text {
    font-size: 17px;
    line-height: 1.7;
    opacity: 0.85;
    margin-bottom: 24px;
}

.info-items {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 28px;
}

.info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.info-icon {
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
}

.info-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.6;
    margin-bottom: 2px;
}

.info-value {
    font-size: 15px;
    font-weight: 500;
}

.btn-info {
    display: inline-block;
    border: 2px solid #2a9d6e;
    color: #2a9d6e !important;
    padding: 11px 28px;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none !important;
    transition: background 0.2s, color 0.2s;
    &:hover {
        background: #2a9d6e;
        color: #fff !important;
    }
}

.form-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #ddd;
}
.form-sub {
    font-size: 14px;
    opacity: 0.6;
    margin: -8px 0 16px;
}

// Waitlist
.waitlist-banner {
    background: #2a1f00;
    border: 1px solid #b87d00;
    color: #f5c842;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 20px;
}

// Guests
.guests-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.guests-actions {
    display: flex;
    gap: 8px;
}

.guest-add-btn {
    font-size: 12px;
    font-weight: 600;
    color: #2a9d6e;
    cursor: pointer;
    padding: 4px 10px;
    border: 1px solid #2a9d6e;
    border-radius: 20px;
    transition: background 0.2s;
    &:hover {
        background: #2a9d6e;
        color: #fff;
    }
}

.guests-empty {
    font-size: 14px;
    opacity: 0.4;
    padding: 16px 0;
    text-align: center;
    border: 1px dashed #333;
    border-radius: 8px;
}

.guest-card {
    background: #1a1a24;
    border: 1px solid #2a2a35;
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 10px;
}

.guest-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}

.guest-type-badge {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 2px 10px;
    border-radius: 20px;
    &.adult {
        background: #1e3a2e;
        color: #2a9d6e;
    }
    &.child {
        background: #2a2050;
        color: #8a6ef5;
    }
}

.guest-remove {
    font-size: 14px;
    opacity: 0.4;
    cursor: pointer;
    &:hover {
        opacity: 1;
    }
}

.sleep-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
}

.sleep-option {
    font-size: 12px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid #333;
    background: #1e1e26;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    &:hover {
        border-color: #555;
    }
    &.active {
        background: #8a6ef5;
        border-color: #8a6ef5;
        color: #fff;
    }
}

// Fields
.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.7;
    margin-bottom: 6px;
}

.field-input {
    background: #1e1e26 !important;
    border: 1px solid #333 !important;
    color: #fff !important;
    border-radius: 6px;
    height: 48px;
    &:focus {
        border-color: #2a9d6e !important;
        outline: none;
    }
}

.input-error {
    border-color: #f44336 !important;
}
.field-error {
    font-size: 12px;
    color: #f44336;
    margin-top: 4px;
}
.consent-text {
    font-size: 13px;
    opacity: 0.5;
}
</style>
